import { SystemOptionType } from 'data/types';
import { FILTER_MISMATCH_TAG } from 'utils/filter-mismatch';

export const SCENARIO_PREMIUM = 'premium';
export const SCENARIO_BUDGET = 'budget';
export const SCENARIO_EASY_INSTALL = 'easy_install';
export const SCENARIO_QUIET_OPERATION = 'quiet_operation';

export type PickKind =
  | 'bestOverall'
  | 'bestComfort'
  | 'lowestCost'
  | 'easiestInstall'
  | 'quietestOperation';

export const PICK_DEFINITIONS: ReadonlyArray<{
  kind: PickKind;
  label: string;
  description: string;
}> = [
  {
    kind: 'bestOverall',
    label: 'Best Overall',
    description: 'Top score for your customer values.'
  },
  {
    kind: 'bestComfort',
    label: 'Most Comfortable',
    description: 'Highest comfort and performance focus.'
  },
  {
    kind: 'lowestCost',
    label: 'Best Value',
    description: 'Best value for the price.'
  },
  {
    kind: 'easiestInstall',
    label: 'Easiest Installation',
    description: 'Lowest installation complexity for your home.'
  },
  {
    kind: 'quietestOperation',
    label: 'Quietest Operation',
    description: 'Lowest sound levels in operation.'
  }
];

export type TopPicks = {
  bestOverall: SystemOptionType | null;
  bestComfort: SystemOptionType | null;
  lowestCost: SystemOptionType | null;
  easiestInstall: SystemOptionType | null;
  quietestOperation: SystemOptionType | null;
  list: SystemOptionType[];
};

export function pickEligibleSystems(
  options: SystemOptionType[]
): SystemOptionType[] {
  return options.filter((o) => !o.tags.includes(FILTER_MISMATCH_TAG));
}

const totalScore = (o: SystemOptionType): number => o.score?.total ?? 0;
const scenarioScore = (o: SystemOptionType, code: string): number =>
  o.score?.totalsByScenario?.[code] ?? 0;

export const preferCandidate = (
  candidate: SystemOptionType,
  incumbent: SystemOptionType | null,
  scorer: (o: SystemOptionType) => number,
  opts: { preferMatching?: boolean } = {}
): boolean => {
  if (!incumbent) return true;
  if (opts.preferMatching) {
    const candidateMatching = candidate.isMatchingAirHandlers ? 1 : 0;
    const incumbentMatching = incumbent.isMatchingAirHandlers ? 1 : 0;
    if (candidateMatching !== incumbentMatching)
      return candidateMatching > incumbentMatching;
  }
  const candidateScore = scorer(candidate);
  const incumbentScore = scorer(incumbent);
  if (candidateScore !== incumbentScore) return candidateScore > incumbentScore;
  return candidate.price < incumbent.price;
};

function pickHighest(
  options: SystemOptionType[],
  scorer: (o: SystemOptionType) => number,
  excludeIds: Set<string>,
  opts: { preferMatching?: boolean } = {}
): SystemOptionType | null {
  let best: SystemOptionType | null = null;
  for (const option of options) {
    if (
      !excludeIds.has(option.id) &&
      preferCandidate(option, best, scorer, opts)
    ) {
      best = option;
    }
  }
  if (best && scorer(best) <= 0) return null;
  return best;
}

export function rankTopPicks(
  options: SystemOptionType[],
  winnersByCode?: Map<string, SystemOptionType>,
  opts: { preferMatching?: boolean } = {}
): TopPicks {
  if (options.length === 0 && (!winnersByCode || winnersByCode.size === 0)) {
    return {
      bestOverall: null,
      bestComfort: null,
      lowestCost: null,
      easiestInstall: null,
      quietestOperation: null,
      list: []
    };
  }

  const used = new Set<string>();
  const preferMatching = opts.preferMatching === true;

  const bestOverall = pickHighest(options, totalScore, used, {
    preferMatching
  });
  if (bestOverall) used.add(bestOverall.id);

  const slotForScenario = (code: string): SystemOptionType | null => {
    const scorer = (o: SystemOptionType) => scenarioScore(o, code);
    const seeded = winnersByCode?.get(code);
    if (
      preferMatching &&
      seeded &&
      !seeded.isMatchingAirHandlers &&
      !used.has(seeded.id)
    ) {
      const tieredFallback = pickHighest(options, scorer, used, {
        preferMatching: true
      });
      if (tieredFallback && tieredFallback.isMatchingAirHandlers) {
        used.add(tieredFallback.id);
        return tieredFallback;
      }
    }
    if (seeded && !used.has(seeded.id)) {
      used.add(seeded.id);
      return seeded;
    }
    const fallback = pickHighest(options, scorer, used, { preferMatching });
    if (fallback) used.add(fallback.id);
    return fallback;
  };

  const bestComfort = slotForScenario(SCENARIO_PREMIUM);
  const lowestCost = slotForScenario(SCENARIO_BUDGET);
  const easiestInstall = slotForScenario(SCENARIO_EASY_INSTALL);
  const quietestOperation = slotForScenario(SCENARIO_QUIET_OPERATION);

  const list: SystemOptionType[] = [];
  if (bestOverall) list.push(bestOverall);
  if (bestComfort) list.push(bestComfort);
  if (lowestCost) list.push(lowestCost);
  if (easiestInstall) list.push(easiestInstall);
  if (quietestOperation) list.push(quietestOperation);

  return {
    bestOverall,
    bestComfort,
    lowestCost,
    easiestInstall,
    quietestOperation,
    list
  };
}

export function sortByOverallScore(
  options: SystemOptionType[],
  opts: { preferMatching?: boolean } = {}
): SystemOptionType[] {
  return [...options].sort((a, b) => {
    if (opts.preferMatching) {
      const aMatching = a.isMatchingAirHandlers ? 1 : 0;
      const bMatching = b.isMatchingAirHandlers ? 1 : 0;
      if (aMatching !== bMatching) return bMatching - aMatching;
    }
    const diff = totalScore(b) - totalScore(a);
    if (diff !== 0) return diff;
    return a.price - b.price;
  });
}

export function pickKindForSystem(
  picks: TopPicks,
  system: SystemOptionType | null
): PickKind | null {
  if (!system) return null;
  if (picks.bestOverall?.id === system.id) return 'bestOverall';
  if (picks.bestComfort?.id === system.id) return 'bestComfort';
  if (picks.lowestCost?.id === system.id) return 'lowestCost';
  if (picks.easiestInstall?.id === system.id) return 'easiestInstall';
  if (picks.quietestOperation?.id === system.id) return 'quietestOperation';
  return null;
}

export function pickLabelFor(kind: PickKind): string {
  return PICK_DEFINITIONS.find((p) => p.kind === kind)?.label ?? '';
}

export function pickSystemNameFor(kind: PickKind): string {
  const label = pickLabelFor(kind);
  return label ? `${label} System` : '';
}

export function pickDescriptionFor(kind: PickKind): string {
  return PICK_DEFINITIONS.find((p) => p.kind === kind)?.description ?? '';
}

export function getSystemHeatingMinTemp(
  system: SystemOptionType
): number | null {
  const minTemps = system.condenserCoverage
    .map((c) => c.minTempAtHeatingScore)
    .filter((t): t is number => t !== null);
  if (minTemps.length === 0) return null;
  return Math.round(Math.max(...minTemps));
}

export function getSystemCoolingMaxTemp(
  system: SystemOptionType
): number | null {
  const maxTemps = system.condenserCoverage
    .map((c) => c.maxTempAtCoolingScore)
    .filter((t): t is number => t !== null);
  if (maxTemps.length === 0) return null;
  return Math.round(Math.min(...maxTemps));
}
